import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardEnhanced = () => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState({ submittals: [], rfis: [], change_orders: [], total_tasks: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchTasks();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/user/tasks`);
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to load tasks');
    }
  };

  const navigateToTask = (task) => {
    const tabMap = {
      submittal: 'submittals',
      rfi: 'rfis',
      change_order: 'change-orders'
    };
    const tab = tabMap[task.task_type] || 'dashboard';
    navigate(`/projects/${task.project_id}?tab=${tab}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Combine all tasks for the list
  const allTasks = [
    ...(tasks.submittals || []),
    ...(tasks.rfis || []),
    ...(tasks.change_orders || [])
  ].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      <div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-slate-900">Dashboard</h1>
        <p className="mt-2 text-base text-slate-600">Overview of your projects and tasks</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-5" data-testid="stat-card-projects">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Total Projects</div>
          <div className="text-3xl font-bold text-slate-900">{projects.length}</div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 shadow-sm p-5" data-testid="stat-card-tasks">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">My Tasks</div>
          <div className="text-3xl font-bold text-blue-900">{tasks.total_tasks}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 shadow-sm p-5" data-testid="stat-card-submittals">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">My Submittals</div>
          <div className="text-3xl font-bold text-amber-900">{tasks.submittals?.length || 0}</div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 shadow-sm p-5" data-testid="stat-card-rfis">
          <div className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-2">My RFIs</div>
          <div className="text-3xl font-bold text-green-900">{tasks.rfis?.length || 0}</div>
        </div>
      </div>

      {/* My Tasks Section - List Format */}
      {tasks.total_tasks > 0 && (
        <div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">My Assigned Tasks</h2>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full" data-testid="tasks-table">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Project</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {allTasks.map((task) => (
                  <tr key={`${task.task_type}-${task.id}`} className="border-b hover:bg-slate-50" data-testid={`task-row-${task.id}`}>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        task.task_type === 'submittal' ? 'bg-amber-100 text-amber-700' :
                        task.task_type === 'rfi' ? 'bg-green-100 text-green-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {task.task_type === 'submittal' ? 'Submittal' : 
                         task.task_type === 'rfi' ? 'RFI' : 'Change Order'}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-slate-900">{task.title}</td>
                    <td className="p-4 text-sm text-slate-600">{task.project_name}</td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        task.priority === 'high' ? 'bg-red-100 text-red-700' :
                        task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">{formatDate(task.due_date)}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        {task.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => navigateToTask(task)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                        data-testid={`go-to-task-${task.id}`}
                      >
                        Go to Task
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">Recent Projects</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.slice(0, 6).map((project) => (
            <a key={project.id} href={`/projects/${project.id}`} className="rounded-lg border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow p-6 block" data-testid={`project-card-${project.id}`}>
              <h3 className="text-xl font-bold text-slate-800 mb-2">{project.name}</h3>
              <p className="text-sm text-slate-600">{project.description || 'No description'}</p>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};